import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

export type TabListProps = Omit<React.ComponentProps<'div'>, 'style'>;

/*
 * Section tabs: for switching content sections within one page (settings,
 * organisation policy, template detail).
 *
 * Not to be confused with view tabs, which switch between modes of a single
 * object and are pill-shaped. Content sections underline; object views pill.
 * Never both on one page.
 */
export function TabList({ className, ...props }: TabListProps) {
  return (
    <div
      data-slot='tab-list'
      role='tablist'
      className={cn('flex flex-wrap gap-1 border-b border-line', className)}
      {...props}
    />
  );
}

/*
 * Exported as variants because the element is the consumer's: a router Link
 * when tabs are routes, a button when they are local state.
 *
 * Inactive labels are ink-muted, not ink-faint. The prototypes used the faint
 * token here and it measures 2.21:1 — below the 4.5:1 floor. The active tab
 * carries navy text AND a green underline, so the distinction survives on two
 * channels without the inactive label having to be pale enough to fail.
 */
export const tabVariants = cva(
  [
    'mr-5.5 border-b-2 px-1 py-3 text-base font-semibold whitespace-nowrap transition-colors',
    'aria-disabled:opacity-40 aria-disabled:hover:text-ink-muted',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      active: {
        true: 'border-accent text-ink',
        false: 'border-transparent text-ink-muted hover:text-ink',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type TabVariants = VariantProps<typeof tabVariants>;

export interface TabProps
  extends Omit<React.ComponentProps<'button'>, 'style'>, TabVariants {}

/*
 * The button half of the pair, for tabs that are local state rather than
 * routes. It exists because app code may not write a raw `<button>` — the
 * design-system lint forbids it — so "the element is the consumer's" needs the
 * consumer to have one to reach for.
 *
 * `aria-selected` follows `active` rather than being asked for separately: a
 * tab that looks active and does not say so is a tab screen readers cannot find.
 */
export function Tab({ className, active, type, ...props }: TabProps) {
  return (
    <button
      data-slot='tab'
      role='tab'
      type={type ?? 'button'}
      aria-selected={Boolean(active)}
      className={cn(tabVariants({ active }), className)}
      {...props}
    />
  );
}
